import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Swal from "sweetalert2";
import api from "core/interceptors/api";
import { CircularProgress, InputAdornment } from "@mui/material";
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// MUI Icons for Adornments
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PercentIcon from '@mui/icons-material/Percent';
import EventIcon from '@mui/icons-material/Event';

export default function CouponEditModal({ open, handleClose, input, setInput }) {
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    // Local states for ReactDatePicker as it works with Date objects
    const [displayStartDate, setDisplayStartDate] = useState(null);
    const [displayEndDate, setDisplayEndDate] = useState(null);

    useEffect(() => {
        if (open) {
            // Convert date strings from `input` prop to Date objects for ReactDatePicker
            setDisplayStartDate(input.start_date ? new Date(input.start_date) : null);
            setDisplayEndDate(input.end_date ? new Date(input.end_date) : null);
            setErrors({}); // Clear errors when modal opens
            setLoading(false); // Reset loading state
        }
    }, [open, input]); // Depend on `open` and `input` prop for re-initialization

    const validateField = (name, value) => {
        let error = "";
        const trimmedValue = typeof value === 'string' ? value.trim() : value;

        switch (name) {
            case "coupon_id":
                if (!trimmedValue) {
                    error = "Coupon Code is required.";
                }
                break;
            case "percentage":
                const parsedPercentage = parseFloat(value);
                if (isNaN(parsedPercentage) || parsedPercentage < 0 || parsedPercentage > 100) {
                    error = "Discount must be between 0 and 100.";
                }
                break;
            case "start_date": // value here is a Date object
                if (!value) {
                    error = "Start Date is required.";
                } else {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0); // Normalize to start of today

                    const selectedStartDate = new Date(value);
                    selectedStartDate.setHours(0, 0, 0, 0); // Normalize selected date

                    if (selectedStartDate < today) {
                        error = "Start Date cannot be in the past.";
                    }
                }
                break;
            case "end_date": // value here is a Date object
                if (!value) {
                    error = "End Date is required.";
                } else if (displayStartDate) { // Only validate against start date if it's selected
                    const selectedEndDate = new Date(value);
                    selectedEndDate.setHours(0, 0, 0, 0); // Normalize selected date

                    const currentStartDate = new Date(displayStartDate);
                    currentStartDate.setHours(0, 0, 0, 0); // Normalize start date from display state

                    if (selectedEndDate < currentStartDate) {
                        error = "End Date cannot be before Start Date.";
                    }
                }
                break;
            default:
                break;
        }
        return error;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const processedValue = name === "percentage" && value !== "" ? parseFloat(value) : value;

        setInput((prev) => ({ ...prev, [name]: processedValue }));
        setErrors((prev) => ({ ...prev, [name]: validateField(name, processedValue) }));
    };

    // Handler for React DatePicker changes
    const handleDateChange = (date, name) => {
        if (name === "start_date") {
            setDisplayStartDate(date); // Update local state for ReactDatePicker
            // Update the `input` state with formatted date string for API payload
            setInput((prev) => ({ ...prev, start_date: date ? date.toISOString().substring(0, 10) : null }));
            // Re-validate end date immediately for consistency
            setErrors((prev) => ({
                ...prev,
                start_date: validateField("start_date", date),
                end_date: validateField("end_date", displayEndDate), // Pass displayEndDate for validation
            }));
        } else if (name === "end_date") {
            setDisplayEndDate(date); // Update local state for ReactDatePicker
            // Update the `input` state with formatted date string for API payload
            setInput((prev) => ({ ...prev, end_date: date ? date.toISOString().substring(0, 10) : null }));
            // Re-validate start date immediately for consistency
            setErrors((prev) => ({
                ...prev,
                end_date: validateField("end_date", date),
                start_date: validateField("start_date", displayStartDate), // Pass displayStartDate for validation
            }));
        }
    };


    const validateForm = () => {
        let newErrors = {};
        let isValid = true;

        const fieldsToValidate = ['coupon_id', 'percentage', 'start_date', 'end_date'];
        fieldsToValidate.forEach(name => {
            // Use the `displayStartDate` and `displayEndDate` states for validation logic
            const valueToValidate = name === 'start_date' ? displayStartDate :
                (name === 'end_date' ? displayEndDate : input[name]);

            const error = validateField(name, valueToValidate);
            if (error) {
                newErrors[name] = error;
                isValid = false;
            }
        });

        setErrors(newErrors);
        return isValid;
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please correct the highlighted errors in the form.',
            });
            return;
        }

        setLoading(true);
        try {
            const payload = {
                ...input,
                percentage: parseFloat(input.percentage),
                // start_date and end_date are already ISO strings in `input` state
                // due to `handleDateChange` logic.
            };

            const response = await api.put(`/coupons/${input.id}`, payload);

            if (response.data.status === 1) {
                Swal.fire({
                    icon: 'success',
                    title: 'Coupon Updated!',
                    text: response.data.message,
                    timer: 2000,
                    showConfirmButton: false,
                });
                handleClose();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Update Failed',
                    text: response.data.message || "An unknown error occurred.",
                });
            }
        } catch (err) {
            console.error("Error updating coupon:", err.response || err);
            const errorMessage = err.response?.data?.message || "An unexpected error occurred while updating the coupon.";
            Swal.fire({
                icon: 'error',
                title: 'API Error',
                text: errorMessage,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <form onSubmit={handleUpdate}>
                <DialogTitle
                    sx={{
                        backgroundColor: "#1976d2", // A vibrant purple for edit
                        color: "#fff",
                        textAlign: "center",
                        fontWeight: "bold",
                        fontSize: "1.5rem",
                        py: 2,
                        mb: 2,
                    }}
                >
                    Edit Coupon
                </DialogTitle>
                <DialogContent dividers sx={{ backgroundColor: '#f9f9f9' }}>
                    <MDBox pt={1}>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Coupon Code"
                                    name="coupon_id"
                                    value={input.coupon_id || ""}
                                    onChange={handleChange}
                                    fullWidth
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    error={!!errors.coupon_id}
                                    helperText={errors.coupon_id}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LocalOfferIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label="Discount Percentage"
                                    name="percentage"
                                    value={input.percentage || ""}
                                    onChange={handleChange}
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    margin="normal"
                                    required
                                    error={!!errors.percentage}
                                    helperText={errors.percentage}
                                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PercentIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <ReactDatePicker
                                    selected={displayStartDate} // Use local state for ReactDatePicker
                                    onChange={(date) => handleDateChange(date, "start_date")}
                                    dateFormat="yyyy-MM-dd"
                                    customInput={
                                        <TextField
                                            label="Start Date"
                                            fullWidth
                                            variant="outlined"
                                            margin="normal"
                                            required
                                            error={!!errors.start_date}
                                            helperText={errors.start_date}
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <EventIcon color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    }
                                    minDate={new Date()} // Prevent selecting past dates from current day
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <ReactDatePicker
                                    selected={displayEndDate} // Use local state for ReactDatePicker
                                    onChange={(date) => handleDateChange(date, "end_date")}
                                    dateFormat="yyyy-MM-dd"
                                    customInput={
                                        <TextField
                                            label="End Date"
                                            fullWidth
                                            variant="outlined"
                                            margin="normal"
                                            required
                                            error={!!errors.end_date}
                                            helperText={errors.end_date}
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <EventIcon color="action" />
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    }
                                    // End date cannot be before start date, or before today if start_date is not set
                                    minDate={displayStartDate || new Date()}
                                />
                            </Grid>
                        </Grid>
                    </MDBox>
                </DialogContent>
                <DialogActions sx={{ p: '16px 24px', backgroundColor: '#e3f2fd' }}>
                    <MDButton onClick={handleClose} color="secondary" disabled={loading} sx={{ mr: 1 }}>
                        Cancel
                    </MDButton>
                    <MDButton type="submit" color="success" disabled={loading}>
                        {loading ? <CircularProgress size={24} color="inherit" /> : "Update"}
                    </MDButton>
                </DialogActions>
            </form>
        </Dialog>
    );
}